import type {
  HelpAgentAttachmentPreview,
  HelpAgentDraftAttachment,
} from '@/types/attachments';
import type { ChatMessage, ServerMessage } from '@/types/chat';
import { createId } from '@/utils/ids';

export class HelpAgentMessageStore {
  private messages: ChatMessage[] = [];
  private readonly pendingByEntityId = new Map<string, string>();

  get snapshot(): ChatMessage[] {
    return [...this.messages];
  }

  reset(): void {
    this.cleanupMessages(this.messages);
    this.messages = [];
    this.pendingByEntityId.clear();
  }

  destroy(): void {
    this.reset();
  }

  queueOutgoingQuestion(text: string, attachments: readonly HelpAgentDraftAttachment[] = []): string {
    const loadingId = createId('loading');
    const messageAttachments = this.createMessageAttachments(attachments);
    this.messages = [
      ...this.messages,
      this.createMessage('user', text, {
        attachments: messageAttachments,
      }),
      this.createMessage('assistant', '', { id: loadingId, state: 'loading' }),
    ];

    return loadingId;
  }

  markPending(entityId: string, loadingId: string): void {
    this.pendingByEntityId.set(entityId, loadingId);
  }

  hasPendingEntity(entityId: string): boolean {
    return this.pendingByEntityId.has(entityId);
  }

  rejectPending(loadingId: string, errorMessage: string): void {
    this.removeMessageById(loadingId);
    this.messages = [
      ...this.messages,
      this.createMessage('assistant', errorMessage, { state: 'error' }),
    ];
  }

  rejectPendingEntity(entityId: string, errorMessage: string): void {
    const loadingId = this.pendingByEntityId.get(entityId);
    if (!loadingId) {
      this.messages = [
        ...this.messages,
        this.createMessage('assistant', errorMessage, { state: 'error' }),
      ];
      return;
    }

    this.pendingByEntityId.delete(entityId);
    this.rejectPending(loadingId, errorMessage);
  }

  applyServerMessage(message: ServerMessage): void {
    let loadingId = this.pendingByEntityId.get(message.entity_id);
    const requestEntityId = message.request ? `${message.entity_id}-request` : undefined;

    const localPending = !loadingId && message.request
      ? this.findLocalPendingRequest(message.request)
      : null;
    if (localPending) {
      loadingId = localPending;
    }

    if (loadingId && requestEntityId && message.request) {
      this.tagLocalRequestForLoading(loadingId, requestEntityId, message.request);
    }

    if (loadingId) {
      this.removeMessageById(loadingId);
      this.pendingByEntityId.delete(message.entity_id);
    } else if (message.request) {
      const alreadyAdded = this.messages.some((item) => item.entityId === requestEntityId);
      if (!alreadyAdded) {
        this.messages = [
          ...this.messages,
          this.createMessage('user', message.request, {
            entityId: requestEntityId,
          }),
        ];
      }
    }

    if (!message.response) {
      return;
    }

    const alreadyAdded = this.messages.some(
      (item) => item.role === 'assistant' && item.entityId === message.entity_id,
    );

    if (alreadyAdded) {
      return;
    }

    this.messages = [
      ...this.messages,
      this.createMessage('assistant', message.response.content, {
        entityId: message.entity_id,
        createdAt: message.created_at,
        images: message.response.images,
      }),
    ];
  }

  private createMessage(
    role: ChatMessage['role'],
    content: string,
    overrides: Partial<Omit<ChatMessage, 'role' | 'content'>> = {},
  ): ChatMessage {
    return {
      id: overrides.id || createId('msg'),
      role,
      content,
      createdAt: overrides.createdAt || new Date().toISOString(),
      entityId: overrides.entityId,
      attachments: overrides.attachments,
      images: overrides.images,
      state: overrides.state || 'ready',
    };
  }

  private createMessageAttachments(attachments: readonly HelpAgentDraftAttachment[]): HelpAgentAttachmentPreview[] {
    return attachments.map((attachment) => ({
      id: createId('attachment-preview'),
      name: attachment.name,
      size: attachment.size,
      mimeType: attachment.mimeType,
      kind: attachment.kind,
      previewUrl: attachment.kind === 'image' ? URL.createObjectURL(attachment.file) : null,
    }));
  }

  private cleanupMessages(messages: readonly ChatMessage[]): void {
    for (const message of messages) {
      this.cleanupAttachments(message.attachments);
    }
  }

  private cleanupAttachments(attachments?: readonly HelpAgentAttachmentPreview[]): void {
    if (!attachments) {
      return;
    }

    for (const attachment of attachments) {
      if (attachment.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }
  }

  private removeMessageById(id: string): void {
    const nextMessages: ChatMessage[] = [];

    for (const message of this.messages) {
      if (message.id === id) {
        this.cleanupAttachments(message.attachments);
        continue;
      }

      nextMessages.push(message);
    }

    this.messages = nextMessages;
  }

  private findLocalPendingRequest(request: string): string | null {
    for (let index = 0; index < this.messages.length - 1; index += 1) {
      const requestMessage = this.messages[index];
      const loadingMessage = this.messages[index + 1];

      if (
        requestMessage.role === 'user'
        && requestMessage.content === request
        && loadingMessage.role === 'assistant'
        && loadingMessage.state === 'loading'
      ) {
        return loadingMessage.id;
      }
    }

    return null;
  }

  private tagLocalRequestForLoading(loadingId: string, requestEntityId: string, request: string): void {
    for (let index = 1; index < this.messages.length; index += 1) {
      const loadingMessage = this.messages[index];
      const requestMessage = this.messages[index - 1];

      if (
        loadingMessage.id === loadingId
        && requestMessage.role === 'user'
        && requestMessage.content === request
      ) {
        this.messages = this.messages.map((item) => (item.id === requestMessage.id
          ? { ...item, entityId: requestEntityId }
          : item));
        return;
      }
    }
  }
}
